import { Button, Card, CardBody, cn, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, RadioGroup, ScrollShadow, Snippet, Spinner, Tooltip, useDisclosure, useRadio, VisuallyHidden } from '@heroui/react'
import { useFilter } from '@react-aria/i18n'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import CopyButton from 'components/Hero/CopyButton'
import { formatDistanceToNow } from 'date-fns'
import { ChevronUp, FolderMinus, Import, Plus, Search, SquareUser, Trash } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch, useFetch } from 'utils/fetch'
import { objectToKeyValue } from 'utils/formatting/objToKeyValues'
import { formatPersonaConfig } from 'utils/funcs/send-call/parseForJson'

function CustomRadio({ code, selected, ...props }) {
  const {
    Component,
    children,
    description,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps,
  } = useRadio(props)

  return (
    <Component
      {...getBaseProps()}
      className={cn(
        `
          group flex max-w-full flex-col items-center justify-between
          tap-highlight-transparent
        `,
        `
          cursor-pointer gap-2 rounded-lg border border-default p-4
          data-[selected=true]:shadow-lg
        `,
        'data-[selected=true]:border-primary',
      )}
    >
      <div className="inline-flex w-full flex-row-reverse justify-between">
        <VisuallyHidden>
          <input {...getInputProps()} />
        </VisuallyHidden>

        <span {...getWrapperProps()}>
          <span {...getControlProps()} />
        </span>
        <div {...getLabelWrapperProps()}>

          {children && (
            <span {...getLabelProps()}>
              <>
                {children}
                {description && (
                  <span className="pl-2 text-small text-foreground opacity-70">{description}</span>
                )}
              </>
            </span>
          )}
          {props.value && <CopyButton className="my-1 h-5 px-2" color="default" variant="flat" size="sm" value={props.value} displayText={props.value} />}

        </div>
      </div>
      <Snippet
        radius="none"
        variant="flat"
        classNames={{
          base: `${selected ? 'max-h-48 overflow-y-scroll shadow-md' : 'max-h-24 overflow-y-hidden'} flex bg-primary-50 border border-default transition-all flex-col w-full`,
          content: 'w-full gap-0.5',
          pre: 'text-wrap truncate font-light text-xs',
        }}
        hideCopyButton
        hideSymbol
      >
        {Object.entries(code).map(([key, value]) => (
          <span key={key} className="flex gap-x-1">
            <span className="text-primary">{key}</span>
            :
            {
              typeof value === 'object'
                ? JSON.stringify(value)
                : <span className="text-success">{String(value)}</span>
            }
            <br />
          </span>
        ))}
      </Snippet>
    </Component>
  )
}

export default function PersonaManagement({ setActivePreset, formMethods }) {
  // Save Modal State
  const { isOpen: isSaveOpen, onOpen: onSaveOpen, onOpenChange: onSaveOpenChange, onClose: onSaveClose } = useDisclosure()
  const [name, setName] = useState('')

  // Import Modal State
  const { isOpen: isImportOpen, onOpen: onImportOpen, onOpenChange: onImportOpenChange, onClose: onImportClose } = useDisclosure()
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Use form methods from props instead of useFormContext
  const { getValues, reset } = formMethods || {}
  const queryClient = useQueryClient()

  // Fetch configs for import modal
  const { data: configs, isLoading } = useFetch('/call_config/read', { queryKey: ['configs'] })

  // Save persona mutation
  const saveCallConfig = useMemo(() => formatPersonaConfig(getValues()), [isSaveOpen])

  const { mutate: savePersona, isPending: isSaveSubmitting } = useMutation({
    mutationFn: () => $fetch('/call_config', {
      method: 'POST',
      body: {
        name,
        call_config: saveCallConfig,
      },
    }),
    onSuccess: () => {
      toast.success('Persona saved successfully')
    },
    onError: () => {
      toast.error('Failed to save persona')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['configs'] })
      onSaveClose()
    },
  })

  const { mutate: deletePersona, isPending: isDeleteSubmitting } = useMutation({
    mutationFn: () => $fetch(`/call_config`, {
      method: 'DELETE',
      body: {
        id: selectedConfig,
      },
    }),
    onSuccess: () => {
      setSelectedConfig(null)
      queryClient.setQueryData(['configs'], old => old.filter(config => config.id !== selectedConfig))
      toast.success('Persona deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete persona')
    },
  })

  // Import functionality
  const handleConfigSelect = (value) => {
    setSelectedConfig(value)
  }

  const { contains } = useFilter({
    sensitivity: 'base', // Ignore case and diacritics
  })

  const filteredConfigs = useMemo(() => {
    if (!configs)
      return []

    const filtered = !searchQuery.trim()
      ? [...configs]
      : configs.filter((config) => {
          if (selectedConfig === config.id)
            return true
          return contains(config.name, searchQuery)
        })

    return filtered.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at),
    )
  }, [searchQuery, contains, configs, selectedConfig])

  function importPersona() {
    const config = configs.find(config => config.id === selectedConfig)
    const { call_config: { custom_tools, request_data, transfer_list, pathway_ids, analysis_schema, metadata, tools, task, prompt, ...rest } } = config
    reset({
      ...rest,
      request_data: objectToKeyValue(request_data ?? {}),
      transfer_list: objectToKeyValue(transfer_list ?? {}),
      pathway_ids: objectToKeyValue(pathway_ids ?? {}),
      analysis_schema: objectToKeyValue(analysis_schema ?? {}),
      metadata: objectToKeyValue(metadata ?? {}),
      tools: custom_tools?.length > 0 ? custom_tools : tools || [],
      task: prompt ?? task ?? null,
    })
    return config
  }

  const handleImport = () => {
    importPersona()
    toast.success('Persona imported successfully')
    onImportClose()
  }

  const handleUpdate = () => {
    const config = importPersona()
    toast.success('Persona imported for editing')
    setActivePreset(config)
    onImportClose()
  }

  return (
    <>
      <Dropdown classNames={{
        content: 'rounded-sm',
      }}
      >
        <DropdownTrigger>
          <Button
            variant="light"
            disableRipple
            radius="none"
            color="primary"
            endContent={<ChevronUp size={16} />}
            startContent={<SquareUser size={16} />}
            id="persona-dropdown"
          >
            Personas
          </Button>
        </DropdownTrigger>
        <DropdownMenu aria-label="Persona actions">
          <DropdownItem
            key="manage"
            startContent={<Import size={16} />}
            onPress={onImportOpen}
          >
            Use and Manage
          </DropdownItem>
          <DropdownItem
            key="create"
            startContent={<Plus size={16} />}
            onPress={onSaveOpen}
          >
            Create New
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {/* Save Persona Modal */}
      <Modal
        size="3xl"
        isOpen={isSaveOpen}
        radius="sm"
        placement="top-center"
        onOpenChange={onSaveOpenChange}
        classNames={{
          footer: 'ml-auto',
          body: 'w-full',
        }}
      >
        <ModalContent>
          {onClose => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h2 className="flex items-center gap-3 text-2xl font-semibold">
                  Create Persona
                </h2>
                <p className="text-sm font-light text-foreground-400">Save the current call configuration as a persona to reuse across Bland.</p>
              </ModalHeader>
              <ModalBody>
                <Card className="w-full p-2" radius="sm">
                  <CardBody className="flex w-full flex-col gap-5">
                    <Input
                      isRequired
                      label="Name"
                      variant="bordered"
                      radius="sm"
                      labelPlacement="outside"
                      placeholder="Enter a name for your new persona"
                      value={name}
                      onValueChange={setName}
                    />

                    <Snippet
                      radius="none"
                      variant="flat"
                      classNames={{
                        base: 'flex bg-primary-50 border border-default shadow-xs transition-all flex-col w-full max-h-64 overflow-y-scroll',
                        content: 'w-full gap-0.5',
                        pre: 'text-wrap truncate font-light text-xs',
                      }}
                      hideCopyButton
                      hideSymbol
                    >
                      {Object.entries(saveCallConfig).map(([key, value]) => (
                        <span key={key} className="flex gap-x-1">
                          <span className="text-primary">{key}</span>
                          :
                          {
                            typeof value === 'object'
                              ? JSON.stringify(value)
                              : <span className="text-success">{String(value)}</span>
                          }
                          <br />
                        </span>
                      ))}
                    </Snippet>
                  </CardBody>
                </Card>
              </ModalBody>
              <ModalFooter>
                <Button color="default" radius="sm" variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="primary" variant="solid" radius="sm" isDisabled={!name?.length} onPress={savePersona} isLoading={isSaveSubmitting}>
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Import Persona Modal */}
      <Modal size="3xl" isOpen={isImportOpen} onOpenChange={onImportOpenChange} radius="sm">
        <ModalContent>
          {onClose => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h2 className="flex items-center gap-3 text-2xl font-semibold">
                  Personas
                </h2>
                <p className="text-sm font-light text-foreground-400">Populate your call form with an existing persona</p>
              </ModalHeader>
              <ModalBody>
                {isLoading
                  ? (
                      <div className="flex h-[500px] flex-col justify-center">
                        <Spinner />
                      </div>
                    )
                  : filteredConfigs?.length > 0
                    ? (
                        <>
                          <Input
                            autoFocus
                            type="search"
                            radius="sm"
                            variant="bordered"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search for a configuration"
                            startContent={(<Search size={16} />)}
                            className="w-full"
                          />
                          <ScrollShadow className="h-[500px] w-full" hideScrollBar>
                            <RadioGroup
                              value={selectedConfig}
                              onValueChange={handleConfigSelect}
                              className="flex flex-col gap-4"
                            >
                              {filteredConfigs?.map(config => (
                                <CustomRadio
                                  key={config.id}
                                  value={config.id}
                                  description={formatDistanceToNow(new Date(config.created_at), { addSuffix: true })}
                                  code={config.call_config}
                                  selected={selectedConfig === config.id}
                                >
                                  {config.name}
                                </CustomRadio>
                              ))}
                            </RadioGroup>
                          </ScrollShadow>
                        </>
                      )
                    : (
                        <div className={`
                          flex h-[500px] w-full flex-col items-center
                          justify-center gap-2
                        `}
                        >
                          <FolderMinus size={45} className="text-gray-800" />
                          <h1 className={`
                            scroll-m-12 text-xl font-semibold tracking-tighter
                          `}
                          >
                            No configurations found.
                          </h1>
                          <p className="text-sm text-gray-600">You haven't saved any call configurations yet.</p>
                        </div>
                      )}
              </ModalBody>
              <ModalFooter>
                <Button className="mr-auto" color="danger" radius="none" variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Tooltip content="Delete Persona">
                  <Button
                    isIconOnly
                    isDisabled={!selectedConfig}
                    radius="none"
                    type="button"
                    color="danger"
                    variant="light"
                    onPress={deletePersona}
                    isLoading={isDeleteSubmitting}
                  >
                    <Trash size={16} />
                  </Button>
                </Tooltip>
                <Button
                  isDisabled={!selectedConfig}
                  radius="none"
                  type="button"
                  color="default"
                  variant="flat"
                  onPress={handleUpdate}
                >
                  Edit
                </Button>
                <Button
                  isDisabled={!selectedConfig}
                  radius="none"
                  type="button"
                  color="primary"
                  onPress={handleImport}
                >
                  Import
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
